import {
  darfErledigen,
  markierKlasse,
  markierTag,
  pulsDauer,
  pulsSummen,
  pulsUhrzeit,
  pulsZeit,
  PULS_UHR_LABELS,
  PULS_UHREN,
  tagesschluessel,
  type MarkierStufe,
  type PulsStand,
  type PulsTermin,
  type PulsUhr,
  type PulsZeit,
  type TaskStatus,
} from '@netcon-work/shared'
import { useQuery } from '@tanstack/react-query'
import { Eye, EyeOff, Sun } from 'lucide-react'
import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import { cn } from '../../lib/cn'
import {
  heuteSchluessel,
  useMarkieren,
  useOffeneMarkierungen,
  useMarkierungen,
} from '../../lib/markierung'
import { useStarten, useStatusSetzen } from '../../lib/mutationen'
import { usePuls, usePulsBlock, usePulsSchritt, usePulsTermine } from '../../lib/puls'
import { useAufgabenAllerWelten } from '../../lib/welt'
import { sitzungQuery, type Aufgabe } from '../../lib/queries'
import { AufgabenVerweis } from '../AufgabeKontext'
import { MarkierKnoepfe, SonnenMarkeFuer } from '../Markierung'
import { Button } from '../ui/primitives'
import { Wiedersehen } from './Wiedersehen'

/**
 * **Der Fokus-Puls im Circle** ([Z-22](../../../../../docs/phasen/zusatz-22-fokus-puls.md),
 * Mockup [mockup-fokus-pulse.html](../../../../../mockup-fokus-pulse.html)).
 *
 * Nils, 19.10.2026: *„jetzt arbeite ich an dem ding, oder an den 2 dingen – das muss im fokus
 * stehen."* Und danach: *„man muss da auch einfach sein «heute» usw. sehen und das man gerade auf
 * dem handy auch schnell sein «heute» abarbeiten kann."*
 *
 * Das Rad bleibt, was es war: das Bild der Lage. Der Puls legt sich **darüber** – die Mitte wächst
 * zur Karte mit Uhr, das Rad tritt zurück. Hier stehen die Bausteine; zusammengesetzt werden sie in
 * `CirclePipe.tsx`.
 */

export type CircleAnsicht = 'rad' | 'puls'
export type CircleFilter = 'alle' | MarkierStufe

/** Was ein Baustein über eine Aufgabe wissen muss – Titel, Welt, Farbe, Abnahme. */
export interface PulsAufgabe {
  id: string
  title: string
  status: TaskStatus
  welt: string
  farbe: string
  abnahmeDurchId: string | null
  abnahmeDurchName: string | null
  assigneeId: string | null
}

/**
 * **Erledigt – oder zur Abnahme.** Dieselbe Regel wie auf der Jetzt-Karte und im Server
 * (`darfErledigen`): wer selbst bearbeitet hat, setzt eine Aufgabe mit Abnehmer nicht auf
 * erledigt, sondern gibt sie zur Abnahme. Aus dem Puls geht sie in beiden Fällen.
 */
export function abschluss(a: PulsAufgabe | undefined, ichId: string | null | undefined) {
  if (!a || darfErledigen(a, ichId)) return { status: 'erledigt' as TaskStatus, text: '✓ Erledigt' }
  return {
    status: 'review' as TaskStatus,
    text: a.abnahmeDurchName ? `Zur Abnahme (${a.abnahmeDurchName})` : 'Zur Abnahme',
  }
}

/** Eine Sekunde Takt, solange etwas läuft – sonst steht die Uhr. */
export function useJetzt(laeuft: boolean): number {
  const [jetzt, setJetzt] = useState(() => Date.now())
  useEffect(() => {
    if (!laeuft) return
    setJetzt(Date.now())
    const t = window.setInterval(() => setJetzt(Date.now()), 1000)
    return () => window.clearInterval(t)
  }, [laeuft])
  return jetzt
}

/** Ist die Uhr abgelaufen? Eine Sekunde Spiel – angehalten wird sie knapp nach null. */
const abgelaufen = (z: PulsZeit | null) => !!z && z.restMs !== null && z.restMs <= 1000

/** Die große Zahl: Rest, Überzug mit „+" oder – bei offener Uhr – die gelaufene Zeit. */
export function zeitText(z: PulsZeit): string {
  if (z.restMs === null) return pulsUhrzeit(z.laufzeitMs)
  return z.restMs < 0 ? `+${pulsUhrzeit(-z.restMs)}` : pulsUhrzeit(z.restMs)
}

/**
 * **Alles, was der Puls tut – an einer Stelle** (holen, erledigen, zurücklegen …).
 *
 * Der Status der Aufgabe geht über die üblichen Wege (`tasks.starten`, `tasks.statusSetzen`) mit
 * ihren Regeln; der Puls merkt sich nur, woran ich sitze. Scheitert das Starten – die Schleuse ist
 * voll –, lässt der Puls die Aufgabe wieder los und sagt, warum.
 */
export function usePulsHandlungen(aufgaben: ReadonlyMap<string, PulsAufgabe>) {
  const { stand } = usePuls()
  const schritt = usePulsSchritt()
  const block = usePulsBlock()
  const starten = useStarten()
  const status = useStatusSetzen()
  const { data: sitzung } = useQuery(sitzungQuery)
  const [hinweis, setHinweis] = useState<string | null>(null)

  const holen = (id: string, dran = false) => {
    setHinweis(null)
    const a = aufgaben.get(id)
    if (a && a.status !== 'in_arbeit') {
      starten.mutate(
        { id },
        {
          onError: (e) => {
            schritt.mutate({ art: 'verlassen', taskId: id })
            setHinweis(e.message)
          },
        },
      )
    }
    schritt.mutate({ art: 'holen', taskId: id, dran, ...block(stand?.uhr ?? 'termin') })
  }

  /**
   * Erledigt – und war die Uhr schon abgelaufen, beginnt sie für das Nachrückende neu. Das
   * Verlassen zuerst: `statusSetzen` ließe die Aufgabe sonst schon ohne den neuen Block los.
   */
  const erledigen = (id: string, z: PulsZeit | null) => {
    if (stand && (stand.aktivId === id || stand.ruhendId === id)) {
      schritt.mutate({
        art: 'verlassen',
        taskId: id,
        neuerBlock: abgelaufen(z) && stand.aktivId === id ? block(stand.uhr) : null,
      })
    }
    status.mutate({ id, status: abschluss(aufgaben.get(id), sitzung?.id).status })
  }

  return {
    holen,
    erledigen,
    /** `neuerBlock`: die Uhr war abgelaufen – für das Nachrückende beginnt sie neu. */
    verlassen: (id: string, neuerBlock = false) =>
      schritt.mutate({
        art: 'verlassen',
        taskId: id,
        neuerBlock: neuerBlock && stand ? block(stand.uhr) : null,
      }),
    wechseln: () => schritt.mutate({ art: 'wechseln' }),
    pausieren: () => schritt.mutate({ art: 'pausieren' }),
    weiter: () => schritt.mutate({ art: 'weiter' }),
    verlaengern: () => schritt.mutate({ art: 'verlaengern', ms: 15 * 60_000 }),
    uhrStellen: (uhr: PulsUhr) => schritt.mutate({ art: 'uhrStellen', ...block(uhr) }),
    beenden: () => schritt.mutate({ art: 'beenden' }),
    hinweis,
    hinweisWeg: () => setHinweis(null),
    ichId: sitzung?.id ?? null,
  }
}

export type PulsHandlungen = ReturnType<typeof usePulsHandlungen>

/**
 * **Die Reihenfolge der ☀-Liste**: was im Puls ist, was in Arbeit ist, was liegengeblieben ist,
 * dann der Rest wie im Bestand. Abgearbeitet wird von oben.
 */
export function useMarkiertListe(stufe: MarkierStufe, aufgaben: ReadonlyMap<string, PulsAufgabe>) {
  const markierungen = useOffeneMarkierungen()
  const { stand } = usePuls()
  const heute = heuteSchluessel()
  return useMemo(() => {
    const imPuls = (id: string) => stand?.aktivId === id || stand?.ruhendId === id
    const liste: { aufgabe: PulsAufgabe; am: string }[] = []
    for (const [id, am] of markierungen) {
      const a = aufgaben.get(id)
      if (!a || markierKlasse(am, heute) !== stufe) continue
      liste.push({ aufgabe: a, am })
    }
    const rang = (x: { aufgabe: PulsAufgabe; am: string }) =>
      (imPuls(x.aufgabe.id) ? 0 : 4) +
      (x.aufgabe.status === 'in_arbeit' ? 0 : 2) +
      (x.am < heute ? 0 : 1)
    return liste.sort((a, b) => rang(a) - rang(b))
  }, [markierungen, aufgaben, heute, stufe, stand?.aktivId, stand?.ruhendId])
}

/** Für heute markiert und heute erledigt – die Zähler des Fortschritts. */
export function useHeuteErledigt(aufgaben: readonly Aufgabe[]): number {
  const markierungen = useMarkierungen()
  const heute = heuteSchluessel()
  return useMemo(
    () =>
      aufgaben.filter((a) => {
        const am = markierungen.get(a.id)
        return (
          am !== undefined &&
          a.status === 'erledigt' &&
          markierKlasse(am, heute) === 'heute' &&
          tagesschluessel(new Date(a.updatedAt)) === heute
        )
      }).length,
    [aufgaben, markierungen, heute],
  )
}

/* ─────────────────────────── Die Mitte ─────────────────────────── */

/**
 * **Die Mitte als Karte** – Titel, große Zahl, Bogen, Puls.
 *
 * Gemessen in `cqi` (Breite der Mitte): dieselbe Karte ist am Rechner 420 px und auf dem Telefon
 * 230 px breit, und die Schrift soll im selben Verhältnis mitgehen statt umzubrechen.
 */
export function PulsMitte({
  durchmesser,
  stand,
  aufgaben,
  summen,
  jetzt,
  naechstes,
  heuteStand,
  zuRad,
  h,
}: {
  /** In Pixeln, vom Rad vorgegeben – ohne Angabe 62 % der Fläche. */
  durchmesser?: number | undefined
  stand: PulsStand | null
  aufgaben: ReadonlyMap<string, PulsAufgabe>
  summen: ReadonlyMap<string, number>
  jetzt: number
  /** Das nächste ☀ Heute – angeboten, wenn gerade nichts im Puls ist. */
  naechstes: PulsAufgabe | null
  heuteStand: { erledigt: number; gesamt: number }
  zuRad: () => void
  h: PulsHandlungen
}) {
  const aktiv = stand ? aufgaben.get(stand.aktivId) : undefined
  const ruhend = stand?.ruhendId ? aufgaben.get(stand.ruhendId) : undefined
  const z = stand ? pulsZeit(stand, jetzt) : null
  const farbe = z?.endspurt ? 'var(--color-warn)' : (aktiv?.farbe ?? 'var(--color-accent)')
  const markierungen = useMarkierungen()
  const heute = heuteSchluessel()

  const cq = (n: number): CSSProperties => ({ fontSize: `${n}cqi` })
  let inhalt: ReactNode
  if (stand && z) {
    const am = markierungen.get(stand.aktivId)
    const sub = z.pausiert
      ? 'pausiert · Leertaste = weiter'
      : stand.uhr === 'termin' && stand.zielAm
        ? `bis ${uhrzeit(stand.zielAm, stand.extraMs)} · dann ${stand.zielTitel ?? 'Termin'}`
        : stand.uhr === 'offen'
          ? `läuft offen · seit ${uhrzeit(stand.beginn, 0)}`
          : `${stand.uhr}-min-Block${stand.extraMs ? ` +${Math.round(stand.extraMs / 60_000)}` : ''}`
    inhalt = (
      <>
        <div className="flex flex-col items-center gap-[1.4cqi] px-[13cqi]">
          <span className="flex items-center gap-[1.6cqi] font-medium text-ink2" style={cq(4.3)}>
            <i
              className="inline-block size-[2.4cqi] rounded-full"
              style={{ background: aktiv?.farbe }}
            />
            {aktiv?.welt}
            {am && markierKlasse(am, heute) === 'heute' && (
              <span className="text-sonne">· ☀ Heute{am < heute ? ' · liegengeblieben' : ''}</span>
            )}
          </span>
          {aktiv ? (
            <span
              className="line-clamp-2 font-semibold leading-tight tracking-[-0.01em]"
              style={cq(6.6)}
            >
              <AufgabenVerweis aufgabeId={aktiv.id} className="hover:text-accent-deep">
                {aktiv.title}
              </AufgabenVerweis>
            </span>
          ) : (
            <span className="text-muted" style={cq(6)}>
              …
            </span>
          )}
          <span
            className={cn(
              'tabular mt-[1cqi] font-semibold leading-none tracking-[-0.03em]',
              z.ueberzogen && 'text-warn',
            )}
            style={cq(17)}
            aria-live="off"
          >
            {zeitText(z)}
          </span>
          <span className="tabular text-muted" style={cq(4.2)}>
            {sub}
          </span>
          <span className="tabular rounded-full bg-[#f3f2ef] px-[2.6cqi] text-ink2" style={cq(3.9)}>
            heute {pulsDauer(summen.get(stand.aktivId) ?? 0)}
          </span>
        </div>
        {ruhend ? (
          <div className="mt-[4cqi] flex w-[60cqi] flex-col items-center gap-[1cqi] border-t border-line pt-[3.4cqi]">
            <span className="max-w-[58cqi] truncate text-ink2" style={cq(4.4)}>
              <span className="text-muted">ruht · </span>
              {ruhend.title}
            </span>
            <button
              type="button"
              onClick={h.wechseln}
              className="cursor-pointer rounded-full px-[3cqi] py-[0.6cqi] text-accent-deep hover:bg-accent-soft"
              style={cq(3.9)}
            >
              ⇅ wechseln
            </button>
          </div>
        ) : (
          <span
            className="mt-[4cqi] rounded-full border border-dashed border-line2 px-[3cqi] py-[0.6cqi] text-muted"
            style={cq(3.7)}
          >
            ＋ zweites Ding: im Rad antippen
          </span>
        )}
      </>
    )
  } else if (naechstes) {
    inhalt = (
      <div className="flex flex-col items-center gap-[1.6cqi] px-[13cqi]">
        <span
          className="rounded-full bg-sonne-bg px-[3cqi] font-semibold text-sonne"
          style={cq(4.4)}
        >
          ☀ Heute · {heuteStand.erledigt} von {heuteStand.gesamt} erledigt
        </span>
        <span className="mt-[2cqi] text-muted" style={cq(4.2)}>
          Als Nächstes
        </span>
        <span className="line-clamp-2 font-semibold leading-tight" style={cq(6.6)}>
          {naechstes.title}
        </span>
        <span className="flex items-center gap-[1.6cqi] text-ink2" style={cq(4.2)}>
          <i
            className="inline-block size-[2.4cqi] rounded-full"
            style={{ background: naechstes.farbe }}
          />
          {naechstes.welt}
        </span>
        <button
          type="button"
          onClick={() => h.holen(naechstes.id)}
          className="mt-[2.4cqi] cursor-pointer rounded-full bg-accent px-[6cqi] py-[1.6cqi] font-semibold text-white hover:bg-accent-deep"
          style={cq(5.4)}
        >
          ▶ Starten
        </button>
        <button
          type="button"
          onClick={zuRad}
          className="cursor-pointer text-muted hover:text-ink"
          style={cq(3.9)}
        >
          Pause, zurück ins Rad
        </button>
      </div>
    )
  } else {
    inhalt = (
      <div className="flex flex-col items-center gap-[2cqi] px-[14cqi]">
        <span style={cq(12)}>{heuteStand.gesamt ? '☀' : '◎'}</span>
        <span className="font-semibold" style={cq(6.2)}>
          {heuteStand.gesamt ? 'Heute ist geschafft' : 'Nichts im Puls'}
        </span>
        <span className="text-muted" style={cq(4.2)}>
          {heuteStand.gesamt
            ? `${heuteStand.erledigt} von ${heuteStand.gesamt} erledigt`
            : 'Eine Aufgabe im Rad antippen und „In den Puls“ wählen.'}
        </span>
        <button
          type="button"
          onClick={zuRad}
          className="cursor-pointer text-accent-deep"
          style={cq(4.2)}
        >
          zurück ins Rad
        </button>
      </div>
    )
  }

  /** Der Bogen: Umfang des Kreises mit r = 47 im Feld 100 × 100. */
  const UMFANG = 2 * Math.PI * 47
  const laeuft = !!z && !z.pausiert
  return (
    <div
      className={cn(
        'puls-mitte absolute top-1/2 left-1/2 aspect-square -translate-x-1/2 -translate-y-1/2',
        !durchmesser && 'w-[62%] max-w-[480px]',
      )}
      style={durchmesser ? { width: durchmesser } : undefined}
      data-endspurt={z?.endspurt || undefined}
    >
      <svg
        viewBox="0 0 100 100"
        className="pointer-events-none absolute inset-0 overflow-visible"
        aria-hidden
      >
        {z && (
          <>
            <circle
              cx="50"
              cy="50"
              r="49"
              fill="none"
              stroke={farbe}
              strokeWidth="1"
              className={cn('puls-atmen', !laeuft && 'puls-still')}
            />
            <circle
              cx="50"
              cy="50"
              r="47"
              fill="none"
              stroke="var(--color-line)"
              strokeWidth="2.2"
            />
            <circle
              cx="50"
              cy="50"
              r="47"
              fill="none"
              stroke={z.pausiert ? 'var(--color-muted)' : farbe}
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeDasharray={
                z.pausiert
                  ? '0.6 3'
                  : `${(UMFANG * Math.max(0.004, z.anteil)).toFixed(2)} ${UMFANG.toFixed(2)}`
              }
              transform="rotate(-90 50 50)"
              className="transition-[stroke] duration-500"
            />
          </>
        )}
      </svg>
      <div
        className="absolute inset-[4.5%] flex flex-col items-center justify-center overflow-hidden rounded-full bg-surface text-center shadow-[0_0_0_1px_var(--color-line),0_10px_34px_rgba(23,22,20,0.08)]"
        style={{ containerType: 'inline-size' }}
      >
        {inhalt}
      </div>
    </div>
  )
}

/** `12:00` – der Zielzeitpunkt plus Verlängerung, in Ortszeit. */
function uhrzeit(iso: string, extraMs: number): string {
  return new Date(new Date(iso).getTime() + extraMs).toLocaleTimeString('de-DE', {
    hour: '2-digit',
    minute: '2-digit',
  })
}

/* ─────────────────────────── Steuerung ─────────────────────────── */

export function PulsSteuer({
  stand,
  aktiv,
  z,
  h,
  termine,
}: {
  stand: PulsStand
  aktiv: PulsAufgabe | undefined
  z: PulsZeit
  h: PulsHandlungen
  termine: readonly PulsTermin[]
}) {
  const [spaeter, setSpaeter] = useState(false)
  const naechster = useMemo(() => {
    const jetzt = Date.now()
    return termine.find(
      (t) => !t.ganztaegig && t.zeigtAls !== 'free' && new Date(t.beginn).getTime() > jetzt,
    )
  }, [termine])
  return (
    <div className="mt-2 flex flex-col items-center gap-2.5">
      <div className="flex flex-wrap justify-center gap-2">
        <Button
          variant="primary"
          className="!bg-good hover:!bg-[#0a8f0a]"
          onClick={() => h.erledigen(stand.aktivId, z)}
        >
          {abschluss(aktiv, h.ichId).text}
        </Button>
        <Button variant="ghost" onClick={z.pausiert ? h.weiter : h.pausieren}>
          {z.pausiert ? '▶ Weiter' : '⏸ Pause'}
        </Button>
        <Button variant="ghost" onClick={h.verlaengern}>
          +15 min
        </Button>
        <Button
          variant="ghost"
          onClick={() => setSpaeter(true)}
          title="Mit Wiedersehen zurücklegen"
        >
          ↷ Später
        </Button>
        <Button variant="ghost" className="text-ink2" onClick={h.beenden}>
          Puls beenden
        </Button>
      </div>
      <div className="flex flex-wrap items-center justify-center gap-1.5 text-[12.5px]">
        <span className="text-muted">Uhr:</span>
        {PULS_UHREN.map((u) => {
          const gewaehlt = stand.uhr === u
          const ohne = u === 'termin' && !naechster && !gewaehlt
          return (
            <button
              key={u}
              type="button"
              disabled={ohne}
              aria-pressed={gewaehlt}
              onClick={() => h.uhrStellen(u)}
              title={ohne ? 'Heute kein Termin mehr im Kalender' : undefined}
              className={cn(
                'tabular cursor-pointer rounded-full border px-3 py-[3px] disabled:cursor-default disabled:opacity-40',
                gewaehlt
                  ? 'border-[#bcd5f3] bg-accent-soft font-medium text-accent-deep'
                  : 'border-line bg-surface text-ink2 hover:border-line2',
              )}
            >
              {PULS_UHR_LABELS[u]}
              {u === 'termin' &&
                (gewaehlt && stand.zielAm
                  ? ` ${uhrzeit(stand.zielAm, 0)}`
                  : naechster
                    ? ` ${uhrzeit(naechster.beginn, 0)}`
                    : '')}
            </button>
          )
        })}
      </div>
      {spaeter && aktiv && (
        <Wiedersehen
          aufgabeId={aktiv.id}
          titel={aktiv.title}
          schliessen={() => setSpaeter(false)}
          weg={() => h.verlassen(aktiv.id)}
        />
      )}
    </div>
  )
}

/* ─────────────────────────── Ende der Uhr ─────────────────────────── */

/**
 * **Zeit um – wie geht es weiter?** Kein stiller Ablauf: die Uhr hält an und fragt. Die Antworten
 * sind dieselben wie unter der Mitte, nur als Frage gestellt.
 */
export function PulsEnde({
  aktiv,
  heuteMs,
  stand,
  h,
  zu,
}: {
  aktiv: PulsAufgabe
  heuteMs: number
  stand: PulsStand
  h: PulsHandlungen
  zu: () => void
}) {
  const [spaeter, setSpaeter] = useState(false)
  const z = pulsZeit(stand, Date.now())
  if (spaeter) {
    return (
      <Wiedersehen
        aufgabeId={aktiv.id}
        titel={aktiv.title}
        schliessen={() => {
          setSpaeter(false)
          zu()
        }}
        weg={() => h.verlassen(aktiv.id, true)}
      />
    )
  }
  return (
    <div
      className="unter-leiste fixed inset-x-0 bottom-0 z-[60] flex items-end justify-center bg-[rgb(23_22_20/0.28)] sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-label="Zeit um"
      onClick={zu}
    >
      <div
        className="w-full max-w-[440px] rounded-t-card border border-line bg-surface p-5 shadow-lg sm:rounded-card"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-[16px] font-bold">Zeit um · {aktiv.title}</h3>
        <p className="mb-4 mt-1.5 text-[13px] text-ink2">
          Heute {pulsDauer(heuteMs)} im Puls.
          {stand.uhr === 'termin' && stand.zielTitel ? ` Jetzt: ${stand.zielTitel}.` : ''} Wie geht
          es weiter?
        </p>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="primary"
            className="!bg-good hover:!bg-[#0a8f0a]"
            onClick={() => {
              h.erledigen(aktiv.id, z)
              zu()
            }}
          >
            {abschluss(aktiv, h.ichId).text}
          </Button>
          <Button
            variant="ghost"
            onClick={() => {
              h.verlaengern()
              h.weiter()
              zu()
            }}
          >
            +15 min
          </Button>
          <Button variant="ghost" onClick={() => setSpaeter(true)}>
            ↷ Später …
          </Button>
          <Button
            variant="ghost"
            className="text-ink2"
            onClick={() => {
              h.beenden()
              zu()
            }}
          >
            Puls beenden
          </Button>
        </div>
      </div>
    </div>
  )
}

/* ─────────────────────────── ☀ Mein Tag ─────────────────────────── */

const STUFEN: { stufe: MarkierStufe; text: string }[] = [
  { stufe: 'heute', text: 'Heute' },
  { stufe: 'morgen', text: 'Morgen' },
  { stufe: 'uebermorgen', text: 'Übermorgen' },
]

/**
 * **Die ☀-Liste unter dem Rad** – Heute, Morgen, Übermorgen.
 *
 * Auf dem Telefon der Weg, den Tag abzuarbeiten: abhaken, ▶ in den Puls, → auf morgen. Jede
 * Zeile ist mindestens 48 px hoch, damit der Daumen trifft.
 */
export function MeinTagListe({
  aufgaben,
  erledigtHeute,
  summen,
  stand,
  h,
}: {
  aufgaben: ReadonlyMap<string, PulsAufgabe>
  erledigtHeute: number
  summen: ReadonlyMap<string, number>
  stand: PulsStand | null
  h: PulsHandlungen
}) {
  const [stufe, setStufe] = useState<MarkierStufe>('heute')
  const markieren = useMarkieren()
  const heute = heuteSchluessel()
  const listen = {
    heute: useMarkiertListe('heute', aufgaben),
    morgen: useMarkiertListe('morgen', aufgaben),
    uebermorgen: useMarkiertListe('uebermorgen', aufgaben),
  }
  const liste = listen[stufe]
  const gesamt = listen.heute.length + erledigtHeute
  const naechstes = listen.heute.find(
    (x) => x.aufgabe.id !== stand?.aktivId && x.aufgabe.id !== stand?.ruhendId,
  )

  return (
    <section
      className="mt-4 w-full max-w-[640px] rounded-card border border-line bg-surface shadow-task"
      aria-label="Mein Tag"
    >
      <h2 className="flex flex-wrap items-center justify-between gap-2 border-b border-line px-3 py-2">
        <span className="flex gap-1" role="tablist">
          {STUFEN.map(({ stufe: s, text }) => (
            <button
              key={s}
              type="button"
              role="tab"
              aria-selected={stufe === s}
              onClick={() => setStufe(s)}
              className={cn(
                'cursor-pointer rounded-lg px-2.5 py-1 text-[13px] font-medium',
                stufe === s ? 'bg-sonne-bg text-sonne' : 'text-ink2 hover:bg-[#f3f2ef]',
              )}
            >
              {s === 'heute' && <Sun size={12} className="mr-1 inline align-[-1px]" aria-hidden />}
              {text}
              <span className="ml-1 font-normal text-muted">{listen[s].length}</span>
            </button>
          ))}
        </span>
        {stufe === 'heute' && naechstes && !stand && (
          <Button variant="ghost" onClick={() => h.holen(naechstes.aufgabe.id)}>
            ▶ Abarbeiten
          </Button>
        )}
      </h2>
      {stufe === 'heute' && gesamt > 0 && (
        <div className="flex items-center gap-2.5 px-3.5 pb-1 pt-2.5 text-[12.5px] text-ink2">
          <span>
            <b>{erledigtHeute}</b> von {gesamt} erledigt
          </span>
          <div className="h-1.5 flex-1 overflow-hidden rounded bg-[#f0efec]">
            <div
              className="h-full rounded bg-[#f5b400] transition-[width] duration-500"
              style={{ width: `${(erledigtHeute / gesamt) * 100}%` }}
            />
          </div>
        </div>
      )}
      <ul className="px-1.5 pb-2 pt-1">
        {liste.map(({ aufgabe: a, am }) => {
          const imPuls = stand?.aktivId === a.id || stand?.ruhendId === a.id
          const zeit = summen.get(a.id) ?? 0
          return (
            <li
              key={a.id}
              className={cn(
                'flex min-h-12 items-center gap-2.5 rounded-[10px] px-2 py-1.5',
                imPuls ? 'bg-accent-soft' : 'hover:bg-[#faf9f7]',
              )}
            >
              <button
                type="button"
                onClick={() => h.erledigen(a.id, stand ? pulsZeit(stand, Date.now()) : null)}
                aria-label={`${abschluss(a, h.ichId).text}: ${a.title}`}
                title={abschluss(a, h.ichId).text}
                className="grid size-[26px] shrink-0 cursor-pointer place-items-center rounded-full border-2 border-line2 bg-surface text-[13px] text-transparent hover:border-good hover:text-good"
              >
                ✓
              </button>
              <span className="min-w-0 flex-1">
                <AufgabenVerweis
                  aufgabeId={a.id}
                  className="block truncate text-[14px] font-medium hover:text-accent-deep"
                >
                  {a.title}
                </AufgabenVerweis>
                <span className="flex flex-wrap items-center gap-1.5 text-[12px] text-muted">
                  <i
                    className="inline-block size-[7px] rounded-full"
                    style={{ background: a.farbe }}
                  />
                  {a.welt}
                  {imPuls ? (
                    <b className="font-semibold text-accent-deep">· im Puls</b>
                  ) : a.status === 'in_arbeit' ? (
                    <span>· in Arbeit</span>
                  ) : null}
                  {zeit >= 60_000 && <span className="tabular">· ⏱ {pulsDauer(zeit)}</span>}
                  {am < heute && <SonnenMarkeFuer am={am} klein />}
                </span>
              </span>
              {!imPuls && (
                <button
                  type="button"
                  onClick={() => h.holen(a.id)}
                  aria-label={`In den Puls: ${a.title}`}
                  title={
                    stand
                      ? stand.ruhendId
                        ? 'Statt des ruhenden in den Puls'
                        : 'Als zweites Ding dazu'
                      : 'In den Puls'
                  }
                  className="h-[34px] min-w-[38px] shrink-0 cursor-pointer rounded-[9px] border border-[#bcd5f3] bg-accent-soft px-2.5 text-[13px] text-accent-deep"
                >
                  ▶
                </button>
              )}
              <button
                type="button"
                onClick={() =>
                  markieren.mutate({
                    taskIds: [a.id],
                    am: markierTag(stufe === 'heute' ? 'morgen' : 'heute', heute),
                  })
                }
                aria-label={stufe === 'heute' ? `Auf morgen: ${a.title}` : `Auf heute: ${a.title}`}
                title={stufe === 'heute' ? 'Auf morgen' : 'Auf heute'}
                className="h-[34px] min-w-[38px] shrink-0 cursor-pointer whitespace-nowrap rounded-[9px] border border-line bg-surface px-2.5 text-[13px] text-ink2 hover:border-line2 hover:text-ink"
              >
                {stufe === 'heute' ? '→' : '↑'}
                <span className="max-sm:hidden"> {stufe === 'heute' ? 'Morgen' : 'Heute'}</span>
              </button>
            </li>
          )
        })}
      </ul>
      {liste.length === 0 && (
        <p className="px-3.5 pb-3 text-[13px] text-muted">
          {stufe === 'heute'
            ? gesamt > 0
              ? '☀ Heute ist geschafft. Alles Weitere ist Zugabe.'
              : 'Nichts für heute markiert. Im Rad eine Aufgabe antippen und ☀ Heute setzen.'
            : `Nichts für ${stufe === 'morgen' ? 'morgen' : 'übermorgen'} markiert.`}
        </p>
      )}
    </section>
  )
}

/* ─────────────────────────── Zelle im Rad ─────────────────────────── */

/**
 * **Die Liste einer Zelle** – was in dieser Welt in diesem Zeitfenster liegt, mit „In den Puls",
 * Öffnen und ☀ je Zeile.
 *
 * Seit Z-22 ist eine Zelle im Rad eine Fläche mit Zahl (wie im Mockup); die Titel stehen hier,
 * lesbar statt gekrümmt. Bei aktivem ☀-Filter steht zuerst, was passt.
 */
export function ZellenMenue({
  kopf,
  liste,
  filter,
  x,
  y,
  stand,
  aufgaben,
  h,
  oeffnen,
  zu,
}: {
  kopf: string
  liste: PulsAufgabe[]
  filter: CircleFilter
  x: number
  y: number
  stand: PulsStand | null
  aufgaben: ReadonlyMap<string, PulsAufgabe>
  h: PulsHandlungen
  oeffnen: (id: string) => void
  zu: () => void
}) {
  const kasten = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState({ left: x, top: y })
  const markierungen = useMarkierungen()
  const heute = heuteSchluessel()
  useEffect(() => {
    const el = kasten.current
    const eltern = el?.offsetParent as HTMLElement | null
    if (!el || !eltern) return
    setPos({
      left: Math.max(8, Math.min(x + 10, eltern.clientWidth - el.offsetWidth - 8)),
      top: Math.max(8, Math.min(y - 20, eltern.clientHeight - el.offsetHeight - 8)),
    })
  }, [x, y])
  useEffect(() => {
    const weg = (e: MouseEvent | KeyboardEvent) => {
      if (
        e instanceof KeyboardEvent
          ? e.key === 'Escape'
          : !kasten.current?.contains(e.target as Node)
      )
        zu()
    }
    const t = window.setTimeout(() => {
      document.addEventListener('mousedown', weg)
      document.addEventListener('keydown', weg)
    })
    return () => {
      window.clearTimeout(t)
      document.removeEventListener('mousedown', weg)
      document.removeEventListener('keydown', weg)
    }
  }, [zu])

  const stufe = (id: string) => {
    const am = markierungen.get(id)
    return am ? markierKlasse(am, heute) : null
  }
  const sortiert =
    filter === 'alle'
      ? liste
      : [
          ...liste.filter((a) => stufe(a.id) === filter),
          ...liste.filter((a) => stufe(a.id) !== filter),
        ]
  const ruhend = stand?.ruhendId ? aufgaben.get(stand.ruhendId) : undefined
  const knopf = (a: PulsAufgabe) =>
    stand?.aktivId === a.id || stand?.ruhendId === a.id
      ? 'im Puls'
      : !stand
        ? '▶ In den Puls'
        : ruhend
          ? '▶ statt Ruhendem'
          : '▶ dazu'

  return (
    <div
      ref={kasten}
      role="dialog"
      aria-label={kopf}
      className="absolute z-20 w-[340px] max-w-[calc(100%-16px)] rounded-xl border border-line bg-surface shadow-[0_12px_40px_rgba(23,22,20,0.16)]"
      style={pos}
    >
      <div className="flex items-center gap-2 border-b border-line px-3.5 py-2.5 text-[13.5px] font-semibold">
        <i
          className="inline-block size-[9px] shrink-0 rounded-full"
          style={{ background: liste[0]?.farbe }}
        />
        <span className="min-w-0 flex-1 truncate">{kopf}</span>
        <span className="font-normal text-muted">{liste.length}</span>
      </div>
      <ul className="max-h-[320px] overflow-y-auto py-1">
        {sortiert.map((a) => {
          const imPuls = stand?.aktivId === a.id || stand?.ruhendId === a.id
          return (
            <li key={a.id} className="flex flex-col gap-1.5 px-3.5 py-2 hover:bg-[#faf9f7]">
              <button
                type="button"
                onClick={() => {
                  oeffnen(a.id)
                  zu()
                }}
                className="cursor-pointer text-left text-[13.5px] leading-snug hover:text-accent-deep"
              >
                {a.title}
              </button>
              <div className="flex flex-wrap items-center gap-2">
                <MarkierKnoepfe taskIds={[a.id]} kompakt />
                <span className="flex-1" />
                <button
                  type="button"
                  disabled={imPuls}
                  title={ruhend && !imPuls ? `Statt „${ruhend.title}" in den Puls` : undefined}
                  onClick={() => {
                    h.holen(a.id)
                    zu()
                  }}
                  className="cursor-pointer rounded-lg border border-[#bcd5f3] bg-accent-soft px-2.5 py-[3px] text-[12.5px] font-medium whitespace-nowrap text-accent-deep disabled:cursor-default disabled:border-line disabled:bg-transparent disabled:text-muted"
                >
                  {knopf(a)}
                </button>
              </div>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

/**
 * **Die Mitte im Rad** – wie im Mockup: „Puls frei", wie viel ☀ heute liegt, „▶ Abarbeiten" und
 * der Weg zum Laufenden. Läuft ein Puls, steht er hier klein mit Uhr.
 *
 * Die Schrift wächst mit der Mitte (`cqi`), aber nicht unter 9 px – auf dem Telefon ist sie
 * kaum 110 px breit.
 */
export function RadMitte({
  durchmesser,
  stand,
  z,
  titel,
  heuteOffen,
  naechstes,
  inArbeit,
  zumPuls,
  abarbeiten,
  inArbeitZeigen,
}: {
  durchmesser: number
  stand: PulsStand | null
  z: PulsZeit | null
  titel: string | null
  heuteOffen: number
  naechstes: PulsAufgabe | null
  inArbeit: number
  zumPuls: () => void
  abarbeiten: () => void
  inArbeitZeigen: () => void
}) {
  const f = (min: number, cqi: number, max: number): CSSProperties => ({
    fontSize: `clamp(${min}px, ${cqi}cqi, ${max}px)`,
  })
  /**
   * **Zwei Ebenen, nicht eine**: `cqi` misst am nächsten *umgebenden* Container, nie am Element
   * selbst. Stünde `containerType` an derselben Ebene wie die Schrift, fiele `cqi` auf die
   * Fensterbreite zurück – am Rechner 20 px je Einheit, und der Inhalt lief aus der Mitte heraus.
   */
  return (
    <div
      className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-full bg-surface shadow-[0_0_0_1px_var(--color-line),0_8px_28px_rgba(23,22,20,0.07)]"
      style={{ width: durchmesser, height: durchmesser, containerType: 'inline-size' }}
    >
      <div className="flex size-full flex-col items-center justify-center gap-[2.5cqi] text-center">
        {stand && z ? (
          <>
            <span
              className="font-semibold tracking-[0.1em] text-muted uppercase"
              style={f(9, 6.5, 12)}
            >
              Im Puls
            </span>
            <span
              className="line-clamp-2 px-[12cqi] leading-tight font-semibold"
              style={f(10, 8, 15)}
            >
              {titel}
            </span>
            <b
              className={cn('tabular leading-none', z.ueberzogen && 'text-warn')}
              style={f(13, 13, 26)}
            >
              {zeitText(z)}
            </b>
            <button
              type="button"
              onClick={zumPuls}
              className="cursor-pointer rounded-full px-[4cqi] text-accent-deep hover:bg-accent-soft"
              style={f(9, 7, 13)}
            >
              zum Puls ›
            </button>
          </>
        ) : (
          <>
            <span
              className="font-semibold tracking-[0.1em] text-muted uppercase"
              style={f(9, 6.5, 12)}
            >
              Puls frei
            </span>
            {naechstes ? (
              <>
                <span
                  className="rounded-full bg-sonne-bg px-[4cqi] font-medium text-sonne"
                  style={f(9, 7, 13)}
                >
                  ☀ {heuteOffen} heute
                </span>
                <button
                  type="button"
                  onClick={abarbeiten}
                  title={`Mit „${naechstes.title}" beginnen`}
                  className="cursor-pointer rounded-full bg-accent px-[6cqi] py-[1.5cqi] font-semibold text-white hover:bg-accent-deep"
                  style={f(10, 8, 14)}
                >
                  ▶ Abarbeiten
                </button>
              </>
            ) : (
              <span className="font-semibold" style={f(14, 13, 24)}>
                frei
              </span>
            )}
            {inArbeit > 0 && (
              <button
                type="button"
                onClick={inArbeitZeigen}
                className="cursor-pointer rounded-full px-[4cqi] text-accent-deep hover:bg-accent-soft"
                style={f(9, 7, 13)}
              >
                {inArbeit} in Arbeit ›
              </button>
            )}
          </>
        )}
      </div>
    </div>
  )
}

/* ─────────────────────────── Zusammengesetzt ─────────────────────────── */

/**
 * **Die Daten, die alle Puls-Bausteine brauchen** – aus Bestand, Markierung und Puls, einmal
 * gerechnet. `farbeVon` kommt aus dem Rad, damit Welt und Feld dieselbe Farbe tragen.
 */
export function usePulsDaten(
  welten: readonly { id: string; label: string }[],
  farbeVon: (weltId: string | null) => string,
) {
  const { aufgaben: bestand } = useAufgabenAllerWelten()
  const puls = usePuls()
  const termine = usePulsTermine()
  const aufgaben = useMemo(() => {
    const label = new Map(welten.map((w) => [w.id, w.label]))
    const karte = new Map<string, PulsAufgabe>()
    for (const a of bestand) {
      karte.set(a.id, {
        id: a.id,
        title: a.title,
        status: a.status,
        welt: (a.workspaceId && label.get(a.workspaceId)) || '',
        farbe: farbeVon(a.workspaceId ?? null),
        abnahmeDurchId: a.abnahmeDurchId ?? null,
        abnahmeDurchName: a.abnahmeDurchName ?? null,
        assigneeId: a.assigneeId ?? null,
      })
    }
    return karte
  }, [bestand, welten, farbeVon])
  const erledigtHeute = useHeuteErledigt(bestand)
  const laeuft = !!puls.stand && !puls.stand.pausiertSeit
  const jetzt = useJetzt(!!puls.stand)
  /** `jetzt` zählt nur, solange etwas läuft – sonst ändern sich die Summen nicht. */
  const takt = laeuft ? jetzt : 0
  const summen = useMemo(
    () => pulsSummen(puls.abschnitte, takt || Date.now()),
    [puls.abschnitte, takt],
  )
  return { ...puls, aufgaben, termine, erledigtHeute, jetzt, summen }
}

/**
 * **Das Umfeld tritt zurück** – im Puls stehen weder die Abwesenden noch die ganze Terminzeile
 * über dem Rad, nur der nächste Termin. Seit Z-22 (20.10.2026) in der Kopfzeile des Circle, als
 * Pille mit einem Auge daneben, statt als eigene Zeile darüber.
 */
export function PulsUmfeld({ zeigen, umschalten }: { zeigen: boolean; umschalten: () => void }) {
  const termine = usePulsTermine()
  const jetzt = useJetzt(true)
  const naechster = termine
    .filter((t) => !t.ganztaegig && t.zeigtAls !== 'free' && new Date(t.beginn).getTime() > jetzt)
    .sort((a, b) => a.beginn.localeCompare(b.beginn))[0]
  const minuten = naechster
    ? Math.round((new Date(naechster.beginn).getTime() - jetzt) / 60_000)
    : null
  return (
    <span className="flex items-center gap-1.5 text-[12.5px] text-ink2">
      <span
        className={cn(
          'tabular max-w-[260px] truncate rounded-full border bg-surface px-3 py-[3px]',
          minuten !== null && minuten <= 10 ? 'border-warn text-warn' : 'border-line',
        )}
        title={naechster ? `Nächster Termin: ${naechster.betreff}` : undefined}
      >
        {naechster && minuten !== null ? (
          <>
            <b className="font-semibold text-ink">{uhrzeit(naechster.beginn, 0)}</b>{' '}
            {naechster.betreff} · in {minuten} min
          </>
        ) : (
          'Heute kein Termin mehr'
        )}
      </span>
      <button
        type="button"
        onClick={umschalten}
        aria-pressed={zeigen}
        aria-label={zeigen ? 'Umfeld wieder ausblenden' : 'Abwesende und Termine einblenden'}
        title={zeigen ? 'Umfeld wieder ausblenden' : 'Ablenkungen sind ausgeblendet – einblenden'}
        className="cursor-pointer rounded-full p-1.5 text-muted hover:bg-[#f3f2ef] hover:text-ink2"
      >
        {zeigen ? <Eye size={15} aria-hidden /> : <EyeOff size={15} aria-hidden />}
      </button>
    </span>
  )
}
